/**
 * Variant Modal Store
 *
 * Global Alpine.js store for managing the variant selection modal.
 * Used when clicking "+" on product cards to select variants before adding to cart.
 */

export default function variantModalStore() {
  return {
    isOpen: false,
    isLoading: false,
    product: {
      id: null,
      title: '',
      image: '',
      variants: [],
    },
    selectedVariantId: null,
    quantity: 1,

    /**
     * Open the modal with product data
     * @param {Object} productData - Product information
     * @param {number} productData.id - Product ID
     * @param {string} productData.title - Product title
     * @param {string} productData.image - Product image URL
     * @param {Array} productData.variants - Array of variant objects
     */
    open(productData) {
      this.product = {
        id: productData.id,
        title: productData.title,
        image: productData.image || '',
        variants: productData.variants || [],
      };

      // Pre-select first available variant
      const firstAvailable = this.product.variants.find((v) => v.available);
      this.selectedVariantId = firstAvailable ? firstAvailable.id : null;

      this.quantity = 1;
      this.isOpen = true;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    },

    /**
     * Close the modal
     */
    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    /**
     * Select a variant
     * @param {number} variantId - Variant ID to select
     */
    selectVariant(variantId) {
      this.selectedVariantId = variantId;
    },

    /**
     * Get the currently selected variant object
     * @returns {Object|null} Selected variant or null
     */
    get selectedVariant() {
      return this.product.variants.find((v) => v.id === this.selectedVariantId) || null;
    },

    /**
     * Increment quantity
     */
    incrementQty() {
      this.quantity++;
    },

    /**
     * Decrement quantity (minimum 1)
     */
    decrementQty() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },

    /**
     * Add selected variant to cart
     */
    async addToCart() {
      if (!this.selectedVariantId || this.isLoading) return;

      this.isLoading = true;

      try {
        const cart = Alpine.store('cart');
        await cart.addItem(this.selectedVariantId, this.quantity);

        // Dispatch event to update any product cards
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Close modal after successful add
        this.close();
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        this.isLoading = false;
      }
    },
  };
}
